import { Container, Graphics } from 'pixi.js';
import { rnd } from './util';

export type PowerupType = 'health' | 'shield' | 'weapon' | 'special' | 'life';

export interface Powerup {
  type: PowerupType;
  x: number; y: number;
  vx: number; vy: number;
  r: number;
  ttl: number;
  gold: boolean;
  alive: boolean;
  view: Graphics;
}

const TTL = 15;

const COLOR: Record<PowerupType, number> = {
  health: 0x00ff55,
  shield: 0x4488ff,
  weapon: 0xff44ff,
  special: 0xffaa00,
  life: 0xff2244,
};

export class PowerupSystem {
  pool: Powerup[] = [];
  private layer: Container;

  constructor(stage: Container) {
    this.layer = new Container();
    stage.addChild(this.layer);
  }

  spawn(type: PowerupType, x: number, y: number, gold = false): void {
    const v = new Graphics();
    if (gold) {
      v.circle(0, 0, 22);
      v.fill({ color: 0xffdd00, alpha: 0.18 });
      v.circle(0, 0, 12);
      v.fill({ color: 0xffdd00 });
      v.stroke({ width: 3, color: 0xffffff });
      v.circle(0, 0, 6);
      v.fill({ color: 0xffffaa });
    } else if (type === 'life') {
      v.circle(0, 0, 22);
      v.fill({ color: 0xff2244, alpha: 0.22 });
      v.circle(0, 0, 11);
      v.fill({ color: 0xff2244 });
      v.stroke({ width: 2.8, color: 0xffffff });
      v.circle(0, 0, 5);
      v.fill({ color: 0xffaaaa });
    } else {
      v.circle(0, 0, 18);
      v.fill({ color: COLOR[type], alpha: 0.18 });
      v.circle(0, 0, 9);
      v.fill({ color: COLOR[type] });
      v.stroke({ width: 2.4, color: 0xffffff });
    }
    this.layer.addChild(v);
    const p: Powerup = {
      type, x, y,
      vx: rnd(-80, 80),
      vy: rnd(-80, 80),
      r: gold ? 14 : 12,
      ttl: TTL,
      gold,
      alive: true,
      view: v,
    };
    p.view.x = x; p.view.y = y;
    this.pool.push(p);
  }

  update(dt: number, W: number, H: number, magnet?: { x: number; y: number; r: number }): void {
    for (let i = this.pool.length - 1; i >= 0; i--) {
      const p = this.pool[i];
      if (!p.alive) {
        p.view.parent?.removeChild(p.view);
        p.view.destroy();
        this.pool.splice(i, 1);
        continue;
      }
      if (magnet) {
        const dx = magnet.x - p.x, dy = magnet.y - p.y;
        const d = Math.hypot(dx, dy);
        if (d < magnet.r) {
          p.vx += (dx / (d || 1)) * 200 * dt;
          p.vy += (dy / (d || 1)) * 200 * dt;
        }
      }
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vx *= Math.pow(0.92, dt * 60);
      p.vy *= Math.pow(0.92, dt * 60);
      p.ttl -= dt;
      if (p.ttl <= 0 || p.x < -40 || p.x > W + 40 || p.y < -40 || p.y > H + 40) {
        p.alive = false;
        continue;
      }
      p.view.x = p.x; p.view.y = p.y;
      p.view.alpha = p.ttl > 1.5 ? 1 : p.ttl / 1.5;
    }
  }
}

export function rollPowerup(kind: string): PowerupType | null {
  const r = Math.random();
  if (kind === 'tank')    return r < 0.03 ? 'life' : r < 0.42 ? 'weapon' : r < 0.65 ? 'health' : r < 0.85 ? 'shield' : 'special';
  if (kind === 'gunship') return r < 0.03 ? 'life' : r < 0.52 ? 'weapon' : r < 0.75 ? 'health' : r < 0.89 ? 'shield' : 'special';
  if (kind === 'bomber')  return r < 0.5 ? 'weapon' : r < 0.75 ? 'health' : 'shield';
  if (kind === 'fighter') return r < 0.55 ? 'weapon' : r < 0.85 ? 'health' : 'shield';
  if (kind === 'carrier') return r < 0.5 ? 'weapon' : r < 0.75 ? 'shield' : 'health';
  if (kind === 'stealth') return r < 0.6 ? 'weapon' : 'health';
  if (kind === 'interceptor') return r < 0.5 ? 'weapon' : 'shield';
  if (kind === 'scout')   return r < 0.30 ? 'weapon' : r < 0.45 ? 'health' : null;
  if (kind === 'drone')   return r < 0.15 ? 'weapon' : r < 0.22 ? 'health' : null;
  if (kind === 'asteroid') return r < 0.12 ? 'health' : null;
  if (kind === 'miniboss') return r < 0.10 ? 'life' : r < 0.50 ? 'special' : 'weapon';
  if (kind === 'boss')    return r < 0.20 ? 'life' : r < 0.55 ? 'special' : 'weapon';
  return null;
}
